import { useEffect, useState } from 'react';
import { Play, X } from 'lucide-react';
import { getShorts } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

/* ─── Reel popup modal (YouTube / Instagram / direct video) ─── */
function VideoModal({ video, onClose }) {
  if (!video) return null;
  const url = video.video_url || video.embed_url || '';
  const isYT = /youtube\.com|youtu\.be/i.test(url);
  const isInsta = /instagram\.com/i.test(url);
  let embedSrc = '';
  if (isYT) {
    const vid = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([a-zA-Z0-9_-]{6,})/)?.[1];
    embedSrc = vid ? `https://www.youtube.com/embed/${vid}?autoplay=1` : '';
  } else if (isInsta) {
    embedSrc = url.replace(/\/?$/, '/embed/');
  }
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={onClose} data-testid="video-popup">
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/25" data-testid="video-popup-close">
        <X className="w-5 h-5" />
      </button>
      <div className="relative w-full max-w-[420px] aspect-[9/16] rounded-2xl overflow-hidden bg-black shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {embedSrc ? (
          <iframe src={embedSrc} className="w-full h-full" frameBorder="0" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title="video" />
        ) : url ? (
          <video src={url} controls autoPlay className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/60 text-sm">No playable source</div>
        )}
      </div>
    </div>
  );
}

function ReelCard({ short, isKannada, onPlay }) {
  const link = short.video_url || short.embed_url;
  const handleCardClick = () => { if (link) window.open(link, '_blank', 'noopener'); };
  const handlePlay = (e) => { e.stopPropagation(); e.preventDefault(); onPlay(short); };

  return (
    <div
      className="group bg-black rounded-xl overflow-hidden cursor-pointer relative hover:shadow-xl transition-shadow"
      onClick={handleCardClick}
      data-testid={`video-${short.short_id}`}
    >
      <div className="relative aspect-[9/16]">
        {short.thumbnail ? (
          <img src={short.thumbnail} alt="" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-700 to-black flex items-center justify-center">
            <Play className="w-12 h-12 text-white/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <button
          type="button"
          onClick={handlePlay}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Play video"
          data-testid={`video-play-${short.short_id}`}
        >
          <span className="w-16 h-16 rounded-full bg-red-600/90 group-hover:bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-all">
            <Play className="w-7 h-7 text-white ml-1" fill="currentColor" />
          </span>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <h4 className="text-[13px] font-bold line-clamp-3 leading-snug font-kannada">
            {isKannada ? (short.title_kn || short.title) : short.title}
          </h4>
          {short.duration && <span className="text-[11px] text-white/70 mt-1 block">{short.duration}</span>}
        </div>
      </div>
    </div>
  );
}

export default function VideosPage() {
  const { isKannada } = useLanguage();
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Load a large page so all published shorts are available. If the site
    // eventually has 200+ videos we should switch to real pagination.
    getShorts({ limit: 200, status: 'published' })
      .then(r => setShorts(Array.isArray(r?.data?.shorts) ? r.data.shorts : []))
      .catch(() => setShorts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#f8f8f8] min-h-screen" data-testid="videos-page">
      <div className="max-w-[1400px] mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-600 to-pink-600 flex items-center justify-center shadow-lg">
            <Play className="w-6 h-6 text-white" fill="currentColor" />
          </span>
          <div>
            <h1 className="text-3xl font-black text-[#1a1a1a] font-kannada">
              {isKannada ? 'ವೀಡಿಯೊ ಸುದ್ದಿಗಳು' : 'Video News'}
            </h1>
            <p className="text-sm text-gray-500">{isKannada ? 'ಎಲ್ಲಾ ಶಾರ್ಟ್ಸ್' : 'All shorts & reels'}</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
              <div key={i} className="bg-gray-200 rounded-xl aspect-[9/16] animate-pulse" />
            ))}
          </div>
        ) : shorts.length === 0 ? (
          <div className="text-center py-20">
            <Play className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-bold mb-1">{isKannada ? 'ಯಾವುದೇ ವೀಡಿಯೊಗಳಿಲ್ಲ' : 'No videos yet'}</p>
            <p className="text-sm text-gray-400">{isKannada ? 'ಸಂಪಾದಕರು ವೀಡಿಯೊಗಳನ್ನು ಪ್ರಕಟಿಸಿದ ನಂತರ ಇಲ್ಲಿ ತೋರಿಸಲಾಗುತ್ತದೆ.' : 'Once editors publish videos they will appear here.'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {shorts.map(s => (
              <ReelCard key={s.short_id} short={s} isKannada={isKannada} onPlay={setActiveVideo} />
            ))}
          </div>
        )}
      </div>
      <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
}
